/**
 * Kinematic character controller: one pure step per unit per tick, ported from Pactsworn's
 * `server/src/character.rs`.
 *
 * A step never moves the capsule into geometry. The whole displacement, vertical included, is swept
 * against the `CollisionWorld` and slid along whatever it hits, so there is no separate "fix it up
 * afterwards" pass. Movement input decides the desired velocity before the step; the step owns
 * gravity, collision response, and grounding.
 */
import type { CapsuleHit, CollisionWorld } from './collision';
import { UNIT_HALF_HEIGHT } from './constants';
import {
  add,
  cross,
  dot,
  horizontal,
  length,
  lengthSq,
  normalizeOrZero,
  scale,
  sub,
  v3,
  type V3,
} from './vec';

/** Gap kept between the capsule and every surface. */
export const SKIN = 0.01;
/** Steepest slope a unit can stand on or walk up. */
export const MAX_WALKABLE_SLOPE_DEG = 50;
/** A unit already on a slope stays on one this much steeper, so it can't flicker off. */
const SLOPE_HYSTERESIS_DEG = 1;
/** A grounded unit follows the floor down this far each tick instead of going airborne. */
export const GROUND_SNAP_DISTANCE = 0.6;
/** Tallest ledge a walking unit climbs without jumping. */
export const STEP_HEIGHT = 0.6;
const MIN_STEP_PROGRESS = 0.05;
/** A unit stands level on a ledge while its axis is at most this far out past the edge. */
export const PERCH_RADIUS = 0.35;
const PERCH_HYSTERESIS = 0.01;
/** Fastest a slide down a too-steep slope carries a unit horizontally. */
export const MAX_SLIDE_SPEED = 5;
export const GRAVITY = 19.29;
export const TERMINAL_VELOCITY = 50;
/**
 * Extra height kept above the floor after grounding. Resting exactly `SKIN` away would start every
 * horizontal cast at its target distance, where the contact normal is noisy enough to report the
 * floor as blocking.
 */
export const GROUND_REST_GAP = 0.5 * SKIN;
const LANDING_REACH = 2 * SKIN;
/** Beyond this sideways shift of the ground probe the unit goes airborne instead. */
const MAX_PROBE_SHIFT = 2 * SKIN;
const LEDGE_INSET = 2 * SKIN;
const MAX_SLIDES = 4;
const MAX_DEPENETRATION_PASSES = 4;
/** Backs the capsule off each surface it hits, so the next cast starts outside its target distance. */
const SLIDE_NUDGE = 1e-3;
const MIN_MOVE = 1e-5;

const WALKABLE_COS = Math.cos((MAX_WALKABLE_SLOPE_DEG * Math.PI) / 180);
const STILL_WALKABLE_COS = Math.cos(
  ((MAX_WALKABLE_SLOPE_DEG + SLOPE_HYSTERESIS_DEG) * Math.PI) / 180,
);
const HOLDING_COS = Math.cos((2 * SLOPE_HYSTERESIS_DEG * Math.PI) / 180);

export interface CharacterState {
  /** Capsule center. */
  position: V3;
  /** Desired velocity: horizontal from input, vertical carried over plus any jump impulse. */
  velocity: V3;
  grounded: boolean;
  /** Floor normal from the last step; only meaningful while grounded. */
  groundNormal: V3;
}

export interface Ground {
  normal: V3;
  collider: number;
}

export interface StepResult {
  position: V3;
  velocity: V3;
  ground: Ground | null;
}

interface Ledge {
  /** Height of the face just past the edge. */
  top: number;
  normal: V3;
  collider: number;
  /** Horizontal distance from the capsule's axis out to the edge. */
  overhang: number;
}

export function step(
  world: CollisionWorld,
  state: CharacterState,
  dt: number,
): StepResult {
  let position = depenetrate(world, state.position);
  let velocity = state.velocity;
  const walking = state.grounded;

  let delta: V3;
  if (walking) {
    velocity = v3(velocity.x, 0, velocity.z);
    delta = alongFloor(scale(horizontal(velocity), dt), state.groundNormal);
  } else {
    // Average of start and end velocity: exact for constant gravity, so jump height doesn't
    // depend on the tick rate.
    const startVy = velocity.y;
    const endVy = Math.max(startVy - GRAVITY * dt, -TERMINAL_VELOCITY);
    velocity = v3(velocity.x, endVy, velocity.z);
    delta = v3(velocity.x * dt, (startVy + endVy) * 0.5 * dt, velocity.z * dt);
  }

  const holding = walking ? state.groundNormal : null;
  [position, velocity] = moveAndSlide(
    world,
    position,
    delta,
    velocity,
    holding,
  );

  let ground: Ground | null = null;
  if (velocity.y <= 0) {
    const reach = walking ? GROUND_SNAP_DISTANCE : LANDING_REACH;
    const found = probeGround(world, position, reach, holding);
    if (found) {
      position = found.rest;
      velocity = v3(velocity.x, 0, velocity.z);
      ground = found.ground;
    }
  }

  return { position, velocity, ground };
}

/**
 * Finds walkable floor up to `reach` below `start` and returns where the capsule rests on it.
 * Slides down past walls and steep slopes the capsule touches. On a ledge's edge the capsule stands
 * level with the ledge while within `PERCH_RADIUS`; a unit already on a floor (`holding`) also stays
 * on a ledge until it clears the edge, if there's floor below to step down onto.
 */
function probeGround(
  world: CollisionWorld,
  start: V3,
  reach: number,
  holding: V3 | null,
): { rest: V3; ground: Ground } | null {
  let position = start;
  let delta = v3(0, -reach, 0);
  for (let i = 0; i < MAX_SLIDES; i++) {
    const hit = world.castCapsule(position, delta, SKIN);
    if (!hit) return null;
    position = add(position, scale(delta, hit.fraction));
    if (length(horizontal(sub(position, start))) > MAX_PROBE_SHIFT) return null;

    const ledge = ledgeBeneath(world, position, hit);
    if (ledge) {
      const footing = holding ? PERCH_RADIUS + PERCH_HYSTERESIS : PERCH_RADIUS;
      const wasOnIt = start.y - UNIT_HALF_HEIGHT >= ledge.top;
      const perched =
        ledge.overhang <= footing ||
        (holding !== null &&
          wasOnIt &&
          floorWithinStepDown(world, position, ledge.top));
      return perched ? standOnLedge(world, position, ledge) : null;
    }
    if (isFloor(hit.normal, holding)) {
      return {
        rest: add(position, v3(0, GROUND_REST_GAP, 0)),
        ground: { normal: hit.normal, collider: hit.collider },
      };
    }
    position = add(position, scale(hit.normal, SLIDE_NUDGE));
    delta = clip(scale(delta, 1 - hit.fraction), hit.normal);
    if (lengthSq(delta) < MIN_MOVE * MIN_MOVE) return null;
  }
  return null;
}

/**
 * The ledge under a capsule centered at `center`, when `hit` is the bottom of the capsule touching
 * the edge or corner of a walkable face that drops away on the capsule's side.
 */
function ledgeBeneath(
  world: CollisionWorld,
  center: V3,
  hit: CapsuleHit,
): Ledge | null {
  if (hit.onFace || hit.normal.y <= 0) return null;
  const toEdge = horizontal(sub(hit.point, center));
  const out = normalizeOrZero(toEdge);
  const above = add(hit.point, v3(0, LEDGE_INSET, 0));
  const face = world.castRay(
    add(above, scale(out, LEDGE_INSET)),
    v3(0, -1, 0),
    2 * LEDGE_INSET,
  );
  if (!face || !walkable(face.normal)) return null;
  // Walkable ground on the near side too makes the edge a ridge to walk over, not a drop.
  const near = world.castRay(
    sub(above, scale(out, LEDGE_INSET)),
    v3(0, -1, 0),
    4 * LEDGE_INSET,
  );
  if (near && walkable(near.normal)) return null;
  return {
    top: above.y - face.distance,
    normal: face.normal,
    collider: face.collider,
    overhang: length(toEdge),
  };
}

/** Raises a capsule resting on a ledge's edge to stand level with the ledge. */
function standOnLedge(
  world: CollisionWorld,
  resting: V3,
  ledge: Ledge,
): { rest: V3; ground: Ground } {
  const standing = ledge.top + UNIT_HALF_HEIGHT + SKIN + GROUND_REST_GAP;
  const lift = v3(0, Math.max(standing - resting.y, 0), 0);
  const fraction =
    lift.y > 0 ? (world.castCapsule(resting, lift, SKIN)?.fraction ?? 1) : 0;
  return {
    rest: add(resting, scale(lift, fraction)),
    ground: { normal: ledge.normal, collider: ledge.collider },
  };
}

function floorWithinStepDown(
  world: CollisionWorld,
  center: V3,
  height: number,
): boolean {
  const floor = world.castRay(
    v3(center.x, height, center.z),
    v3(0, -1, 0),
    GROUND_SNAP_DISTANCE,
  );
  return floor !== null && walkable(floor.normal);
}

function moveAndSlide(
  world: CollisionWorld,
  position: V3,
  delta: V3,
  velocity: V3,
  holding: V3 | null,
): [V3, V3] {
  const planes: V3[] = [];

  for (let i = 0; i < MAX_SLIDES; i++) {
    if (lengthSq(delta) < MIN_MOVE * MIN_MOVE) break;
    const hit = world.castCapsule(position, delta, SKIN);
    if (!hit) {
      position = add(position, delta);
      break;
    }
    position = add(
      add(position, scale(delta, hit.fraction)),
      scale(hit.normal, SLIDE_NUDGE),
    );
    const remaining = scale(delta, 1 - hit.fraction);

    // The contact normal, not the face normal: touching a ledge's edge is not walking on it.
    if (holding && isFloor(hit.normal, holding)) {
      delta = alongFloor(horizontal(remaining), hit.normal);
      continue;
    }

    const feet = position.y - UNIT_HALF_HEIGHT;
    if (holding && hit.point.y - feet <= STEP_HEIGHT) {
      const stepped = stepUp(world, position, horizontal(remaining));
      if (stepped) {
        position = stepped;
        break;
      }
    }

    // Falling onto floor or a ledge's edge lands there: the rest of the tick follows the floor
    // instead of sliding down it or rolling off the edge.
    if (!holding && delta.y < 0) {
      let floor: V3 | null = null;
      if (hit.onFace && walkable(hit.normal)) {
        floor = hit.normal;
      } else {
        const ledge = ledgeBeneath(world, position, hit);
        if (ledge && ledge.overhang <= PERCH_RADIUS) floor = ledge.normal;
      }
      if (floor) {
        velocity = v3(velocity.x, Math.max(velocity.y, 0), velocity.z);
        delta = alongFloor(horizontal(remaining), floor);
        continue;
      }
    }

    // While walking, anything that isn't floor is a vertical wall: sliding along a steep slope
    // can't carry the unit up it, and an edge at head height can't press it into the floor.
    let plane = hit.normal;
    if (holding) {
      const flat = horizontal(hit.normal);
      if (lengthSq(flat) > 1e-12) plane = normalizeOrZero(flat);
    }

    let next = clip(remaining, plane);
    const earlier = planes.find((p) => dot(next, p) < 0);
    if (earlier) {
      const crease = normalizeOrZero(cross(earlier, plane));
      next = scale(crease, dot(next, crease));
    }
    planes.push(plane);
    const steep =
      !holding && hit.onFace && hit.normal.y > 0 && !walkable(hit.normal);
    const deflected = deflect(velocity, plane, steep);
    if (steep) {
      // The rest of this tick slides no faster than the slide itself.
      const free = length(clip(velocity, plane));
      const kept = length(deflected);
      if (free > kept) next = scale(next, kept / free);
    }
    velocity = deflected;
    delta = next;
  }

  return [position, velocity];
}

/** Climbs a ledge up to `STEP_HEIGHT` tall: up, then `forward`, then back down onto floor. */
function stepUp(world: CollisionWorld, position: V3, forward: V3): V3 | null {
  const up = v3(0, STEP_HEIGHT, 0);
  const raised = add(
    position,
    scale(up, world.castCapsule(position, up, SKIN)?.fraction ?? 1),
  );
  const ahead = add(
    raised,
    scale(forward, world.castCapsule(raised, forward, SKIN)?.fraction ?? 1),
  );
  if (length(horizontal(sub(ahead, raised))) < MIN_STEP_PROGRESS) return null;
  const found = probeGround(
    world,
    ahead,
    raised.y - position.y + LANDING_REACH,
    null,
  );
  if (!found) return null;
  return found.rest.y - position.y <= STEP_HEIGHT + SKIN ? found.rest : null;
}

export function depenetrate(world: CollisionWorld, position: V3): V3 {
  for (let i = 0; i < MAX_DEPENETRATION_PASSES; i++) {
    const penetration = world.deepestPenetration(position);
    if (!penetration) break;
    position = add(
      position,
      scale(penetration.normal, penetration.depth + SKIN),
    );
  }
  return position;
}

/** Short enough that a blink's ground snap follows stairs and slopes down. */
const BLINK_STRIDE = 0.5;

/**
 * Walks the capsule the horizontal `offset` at once: along floors, up stairs and steps, sliding
 * along walls without climbing them, and level through the air past a drop. Ends at rest.
 */
export function blink(
  world: CollisionWorld,
  position: V3,
  groundNormal: V3,
  offset: V3,
): StepResult {
  const strides = Math.max(1, Math.ceil(length(offset) / BLINK_STRIDE));
  const stride = scale(horizontal(offset), 1 / strides);
  let result: StepResult = { position, velocity: v3(), ground: null };
  for (let i = 0; i < strides; i++) {
    result = step(
      world,
      {
        position: result.position,
        velocity: stride,
        grounded: true,
        groundNormal,
      },
      1,
    );
    groundNormal = result.ground?.normal ?? v3(0, 1, 0);
  }
  return { ...result, velocity: v3() };
}

export const walkable = (normal: V3): boolean => normal.y >= WALKABLE_COS;

/** Floor under a unit that was already standing (`holding` is last tick's ground normal). */
function isFloor(normal: V3, holding: V3 | null): boolean {
  if (walkable(normal)) return true;
  if (!holding) return false;
  return normal.y >= STILL_WALKABLE_COS && dot(normal, holding) >= HOLDING_COS;
}

/** Tilts a horizontal displacement into the floor plane, keeping its horizontal length. */
function alongFloor(flat: V3, floorNormal: V3): V3 {
  if (floorNormal.y <= Number.EPSILON) return flat;
  const rise =
    -(floorNormal.x * flat.x + floorNormal.z * flat.z) / floorNormal.y;
  return v3(flat.x, rise, flat.z);
}

/** Removes the part of `v` that heads into the surface with normal `normal`. */
const clip = (v: V3, normal: V3): V3 =>
  sub(v, scale(normal, Math.min(dot(v, normal), 0)));

/**
 * Velocity after hitting a surface. Only sliding down a too-steep face turns the hit into
 * horizontal speed, up to `MAX_SLIDE_SPEED` and never upward. Everything else only takes horizontal
 * speed away, so glancing off geometry never launches a unit.
 */
function deflect(velocity: V3, normal: V3, steep: boolean): V3 {
  const clipped = clip(velocity, normal);
  if (steep) {
    const limit = Math.max(length(horizontal(velocity)), MAX_SLIDE_SPEED);
    const slide = length(horizontal(clipped));
    const slid = slide > limit ? scale(clipped, limit / slide) : clipped;
    return v3(slid.x, Math.min(slid.y, Math.max(velocity.y, 0)), slid.z);
  }
  const sideways = clip(
    horizontal(velocity),
    normalizeOrZero(horizontal(normal)),
  );
  return v3(sideways.x, clipped.y, sideways.z);
}
